import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Typography} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import Order, {CardOrder} from "ts/api/order";
import OrdersStepper from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/Stepper";
import CartOrder from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/CartOrder";
import OrderAlert from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/OrderAlert";
import {
  ActiveOrderPaymentStatus,
  NON_APPROVAL_ORDER_GROUP,
  useShoppingSummaryActionsContext,
} from "view/components/ShoppingSummary/context";
import ConfirmModal from "view/components/ConfirmModal";
import {UNPAID_ORDERS_WARNING} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/constants";
import {
  getLastFailedOrderIndex,
  getLastUnpaidOrderIndex,
} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/helpers/multi-step-payment";
import {useDispatch} from "react-redux";
import {resetCartStepper} from "store/exhibitors/cartStepper/slice";
import {useShowDiscountCodesActions} from "store/discount-code/discount-code-store";
import {useCurrentShowId} from "hooks";
import useStyles from "./styles";

export type StepOrder = {
  id: string;
  isPaid: boolean;
  error?: string;
  attempts: number;
};

type OrdersStepperProps = {
  orders: CardOrder[];
  stepperState: StepOrder[];
  handleSingleOrderPayment(order: Order): Promise<void>;
  redirectToSuccess: () => void;
  isPaymentDisabled: boolean;
  isStepperSubmitting: boolean;
  setIsStepperSubmitting: Dispatch<SetStateAction<boolean>>;
  categoryIdsBySupplier: Record<string, Set<string>>;
  newStandId: string;
};

const OrdersList = ({
  orders,
  stepperState,
  handleSingleOrderPayment: propsHandlePayment,
  isPaymentDisabled,
  redirectToSuccess,
  isStepperSubmitting: isSubmitting,
  setIsStepperSubmitting: setIsSubmitting,
  categoryIdsBySupplier,
  newStandId,
}: OrdersStepperProps) => {
  const {t} = useTranslate();
  const classes = useStyles();

  const showId = useCurrentShowId();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [shouldWarnUnpaidOrders, setShouldWarnUnpaidOrders] = useState(false);

  const {updateActiveOrderGroup} = useShoppingSummaryActionsContext();
  const {reset: resetAppliedDiscountCodes} = useShowDiscountCodesActions();
  const {validate: validateDiscountCodes} = useShowDiscountCodesActions();

  const activeOrder = orders[activeStep] ?? null;
  const orderStep = stepperState[activeStep];

  const isAllUnpaidFailed = useMemo(() => {
    const paidLength = stepperState.filter((o) => o.isPaid).length;
    const failedLength = stepperState.filter((o) => o.error).length;
    const isAllUnpaidFailed =
      stepperState.length - paidLength === failedLength && failedLength > 0;

    return isAllUnpaidFailed;
  }, [stepperState]);

  const isOthersUnpaid = stepperState.some(
    (o, idx) => !o.isPaid && idx !== activeStep
  );

  useEffect(() => {
    if (!orderStep || !activeOrder) {
      return;
    }

    const orderProduct = activeOrder.products?.[0];
    if (!orderProduct) {
      return;
    }
    const supplierId = !orderProduct.approval
      ? NON_APPROVAL_ORDER_GROUP
      : orderProduct?.owner?.id;
    if (!supplierId) {
      return;
    }

    const paymentStatus = orderStep.isPaid
      ? ActiveOrderPaymentStatus.Success
      : orderStep.error
      ? ActiveOrderPaymentStatus.Error
      : ActiveOrderPaymentStatus.Idle;

    updateActiveOrderGroup(supplierId, paymentStatus);
  }, [updateActiveOrderGroup, activeOrder, orderStep]);

  if (!activeOrder) {
    return null;
  }

  const isOrderLastUnpaid =
    getLastUnpaidOrderIndex(stepperState) === activeStep;
  const isOrderLastFailed =
    getLastFailedOrderIndex(stepperState) === activeStep;

  const moveToNextOrder = () => {
    const followingOrderIdx = stepperState.findIndex(
      (o, idx) => !o.isPaid && idx > activeStep
    );

    if (followingOrderIdx !== -1) {
      return setActiveStep(followingOrderIdx);
    }

    const firstUnpaidOrderIdx = stepperState.findIndex(
      (o, idx) => !o.isPaid && idx !== activeStep
    );
    if (firstUnpaidOrderIdx !== -1) {
      setActiveStep(firstUnpaidOrderIdx);
    }
  };

  const handlePayment = async () => {
    try {
      const isValid = await validateDiscountCodes(
        showId,
        newStandId,
        categoryIdsBySupplier
      );
      if (!isValid) {
        return;
      }
      setIsSubmitting(true);

      const isUnpaidOrders = stepperState.some(
        (o, idx) => !o.isPaid && idx !== activeStep
      );
      await propsHandlePayment(activeOrder);

      if (isOrderLastUnpaid && !isUnpaidOrders) {
        resetAppliedDiscountCodes(showId);
        redirectToSuccess();
      } else if (isUnpaidOrders) {
        moveToNextOrder();
      }
    } catch (e: any) {
      console.error("Payment failed: ", e?.message);
    } finally {
      setIsSubmitting(false);
      if (shouldWarnUnpaidOrders) {
        setShouldWarnUnpaidOrders(false);
      }
    }
  };

  const handleStepChange = (idx: number) => {
    setActiveStep(idx);
  };

  const onTryToSkip = async () => {
    if (isAllUnpaidFailed && isLastToSkip) {
      return setShouldWarnUnpaidOrders(true);
    }
    handleSkip();
  };

  const handleSkip = () => {
    if (!orderStep?.error) {
      return;
    }

    moveToNextOrder();
  };
  const handleSkipAndRedirect = () => {
    if (stepperState.some((o) => o.isPaid)) {
      return redirectToSuccess();
    }

    dispatch(resetCartStepper());
  };

  const isLastToPay = isOrderLastUnpaid && !isOthersUnpaid;
  const isLastToSkip = isOrderLastFailed && isAllUnpaidFailed;

  return (
    <>
      {shouldWarnUnpaidOrders && (
        <ConfirmModal
          submitVariant="contained"
          titleClassname={classes.modalTitle}
          className={classes.modalRoot}
          title={t(UNPAID_ORDERS_WARNING)}
          onClose={() => setShouldWarnUnpaidOrders(false)}
          onSubmit={handleSkipAndRedirect}
        />
      )}
      <FlexBox column fullWidth gap={2}>
        <Typography variant="subtitle2" className={classes.header}>
          <T keyName="exhibitor.cart.payment.order_list_title" />
        </Typography>
        <OrderAlert />
        <OrdersStepper
          isDisabled={isSubmitting}
          activeStep={activeStep}
          steps={stepperState}
          handleStepChange={handleStepChange}
        />
        <CartOrder
          order={activeOrder}
          orderStep={orderStep}
          onSkip={onTryToSkip}
          onPay={handlePayment}
          isLastToPay={isLastToPay}
          isLastToSkip={isLastToSkip}
          isPaymentDisabled={isPaymentDisabled}
          isSubmitting={isSubmitting}
        />
      </FlexBox>
    </>
  );
};

export default OrdersList;
